import {
    AssessmentMeasurementConfiguration,
    CommandResponse,
    RevisionedCommandResponse,
    toAssessmentMeasurementConfiguration,
    toAssessmentMeasurementConfigurationReference,
} from '@faro/study-designer-model';
import axios from 'axios';
import { Identifiable } from 'faro-common-model';
import { StudyPathParameter } from '../parameter';

export type AssessmentMeasurementConfigurationPathParameters = StudyPathParameter;

export interface AssessmentMeasurementConfigurationQueryParameters {
    revision?: string;
    studyAssessmentMeasurementId?: string;
    isOptionalOnly?: boolean;
}

export const assessmentMeasurementConfigurationClient = {
    list: async (
        query: AssessmentMeasurementConfigurationPathParameters & AssessmentMeasurementConfigurationQueryParameters,
        signal?: AbortSignal
    ): Promise<AssessmentMeasurementConfiguration[]> => {
        const { studyId, ...params } = query;
        const response = await axios.get(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations`,
            {
                params,
                signal,
            }
        );
        return response.data.map(toAssessmentMeasurementConfiguration);
    },
    get: async (
        query: AssessmentMeasurementConfigurationPathParameters & Identifiable,
        signal?: AbortSignal
    ): Promise<AssessmentMeasurementConfiguration> => {
        const { studyId, id, ...params } = query;
        const response = await axios.get(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations/${id}`,
            {
                params,
                signal,
            }
        );
        return toAssessmentMeasurementConfiguration(response.data);
    },
    create: async (
        data: Partial<Omit<AssessmentMeasurementConfiguration, 'id'>>,
        signal?: AbortSignal
    ): Promise<CommandResponse> => {
        const { studyId } = data;
        const response = await axios.post(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations`,
            data,
            { signal }
        );
        return response.data;
    },
    update: async (
        data: AssessmentMeasurementConfigurationPathParameters &
            Partial<AssessmentMeasurementConfiguration> &
            Identifiable,
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<any>> => {
        const { studyId, id } = data;
        const response = await axios.put(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations/${id}`,
            data,
            { signal }
        );
        return response.data;
    },
    delete: async (
        data: AssessmentMeasurementConfigurationPathParameters & Identifiable,
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<any>> => {
        const { studyId, id } = data;
        const response = await axios.delete(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations/${id}`,
            { signal }
        );
        return response.data;
    },
    clone: async (
        command: Partial<AssessmentMeasurementConfiguration> & Identifiable & { sequenceNumber: number },
        signal?: AbortSignal
    ): Promise<{ id: string; revision: number }> => {
        const { studyId, id } = command;
        const response = await axios.post(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations/${id}/actions/clone`,
            command,
            { signal }
        );
        return {
            id: response.data.entityId,
            revision: response.data.revision,
        };
    },
    move: async (
        command: StudyPathParameter & Identifiable & { sequenceNumber: number },
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<any>> => {
        const { studyId, id, sequenceNumber } = command;
        const response = await axios.post(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations/${id}/actions/move`,
            { sequenceNumber },
            { signal }
        );
        return response.data;
    },
    references: async (
        query: StudyPathParameter & AssessmentMeasurementConfigurationQueryParameters,
        signal?: AbortSignal
    ) => {
        const { studyId, ...params } = query;
        const response = await axios.get(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations/actions/get-references`,
            { signal, params }
        );
        return response.data.map(toAssessmentMeasurementConfigurationReference);
    },
    studyCustomTags: async (query: AssessmentMeasurementConfigurationPathParameters, signal?: AbortSignal) => {
        const { studyId } = query;
        const response = await axios.get(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations/actions/study-custom-tags`,
            { signal }
        );
        return response.data;
    },
    reset: async (
        resetCommand: AssessmentMeasurementConfigurationPathParameters & Identifiable
    ): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { studyId, id, ...params } = resetCommand;
        const response = await axios.post(
            `/api/study-designer/studies/${studyId}/assessment-measurement-configurations/${id}/actions/reset`,
            {
                params,
            }
        );
        return response.data;
    },
};
